import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { timelineMessages } from '../messages/timelineMessages';
import type { LaneProps } from './Timeline';
import { secToPercent, showsEdge, spanBox } from './timelineGeometry';
import { useClipMove } from './useClipMove';
import { useEdgeDrag } from './useEdgeDrag';
import { useLaneDrop } from './useLaneDrop';
import styles from './FootageLane.module.scss';

/**
 * The footage lane (design Timeline, c5): one block per clip with the poster of its footage, a
 * violet dot on own footage, the selected clip in violet. A drag of a clip moves it to the beat
 * under the pointer; a drag of a cut between two clips moves the cut on the beat (only when both
 * clips are wide enough to grab: zoom in for the short ones); a footage card dropped here goes in
 * at the beat under the pointer.
 */
const FootageLaneComponent: React.FC<LaneProps> = ({ view, pxPerSec, snapOn, onGuide }) => {
  const intl = useIntl();
  const { doc, footage, selection, select, transport, readOnly } = useMusicVideoEditor();
  const laneRef = useRef<HTMLDivElement>(null);
  const edges = useEdgeDrag({ laneRef, view, snapOn, onGuide });
  const drop = useLaneDrop({ laneRef, view, snapOn, onGuide });
  const move = useClipMove({ laneRef, view, snapOn, onGuide });
  const selectedId = selection?.kind === 'clip' ? selection.id : null;
  const ghostBox = move.ghost === null ? null : spanBox(move.ghost.startSec, move.ghost.endSec, view);
  const ghostClip = move.ghost === null ? undefined : doc.clips.find((clip) => clip.id === move.ghost?.id);
  const ghostPoster = ghostClip === undefined ? null : (footage.lookup(ghostClip.source)?.posterUrl ?? null);

  return (
    <div
      ref={laneRef}
      className={clsx(styles.lane, styles.hFootage, drop.isOver && styles.laneDrop)}
      onDragOver={drop.onDragOver}
      onDragLeave={drop.onDragLeave}
      onDrop={drop.onDrop}
    >
      {doc.clips.map((clip, index) => {
        const box = spanBox(clip.startSec, clip.endSec, view);
        if (box === null) {
          return null;
        }
        const clipFootage = footage.lookup(clip.source);
        const label = clipFootage?.label ?? '';
        return (
          <button
            key={clip.id}
            type="button"
            className={clsx(
              styles.clip,
              !readOnly && styles.clipMovable,
              clip.source.kind === 'footage' && styles.clipMine,
              clip.id === selectedId && styles.clipOn,
              move.ghost?.id === clip.id && styles.clipMoving
            )}
            style={{
              left: `${box.left}%`,
              width: `calc(${box.width}% - 2px)`,
              backgroundImage:
                clipFootage === null || clipFootage.posterUrl === null ? undefined : `url("${clipFootage.posterUrl}")`,
            }}
            aria-pressed={clip.id === selectedId}
            aria-label={intl.formatMessage(timelineMessages.clipLabel, { number: index + 1, label })}
            title={readOnly ? label : intl.formatMessage(timelineMessages.clipTitle, { label })}
            onPointerDown={(event) => move.onPointerDown(clip, event)}
            onPointerMove={move.onPointerMove}
            onPointerUp={move.onPointerUp}
            onPointerCancel={move.onPointerCancel}
            onKeyDown={move.onKeyDown}
            onClick={() => {
              if (move.takeDragEnd()) {
                return;
              }
              select({ kind: 'clip', id: clip.id });
              const now = transport.livePositionSec();
              if (now < clip.startSec || now >= clip.endSec) {
                transport.seek(clip.startSec);
              }
            }}
          />
        );
      })}
      {ghostBox !== null && (
        <div
          className={styles.ghost}
          style={{
            left: `${ghostBox.left}%`,
            width: `calc(${ghostBox.width}% - 2px)`,
            backgroundImage: ghostPoster === null ? undefined : `url("${ghostPoster}")`,
          }}
        />
      )}
      {!readOnly &&
        move.ghost === null &&
        doc.clips.slice(0, -1).map((clip, index) => {
          const left = secToPercent(clip.endSec, view);
          const next = doc.clips[index + 1];
          if (left <= 0 || left >= 100 || next === undefined || !showsEdge(clip, next, pxPerSec)) {
            return null;
          }
          return (
            <button
              key={`edge-${clip.id}`}
              type="button"
              className={styles.edge}
              style={{ left: `${left}%` }}
              aria-label={intl.formatMessage(timelineMessages.edgeLabel, { number: index + 1 })}
              onPointerDown={(event) => edges.onPointerDown(index, event)}
              onPointerMove={(event) => edges.onPointerMove(index, event)}
              onPointerUp={edges.onPointerUp}
              onPointerCancel={edges.onPointerUp}
              onKeyDown={(event) => edges.onKeyDown(index, event)}
            />
          );
        })}
    </div>
  );
};

export const FootageLane = TypedMemo(FootageLaneComponent);
